import type {StatusColors} from '@core/store/SettingsStore';

export type ColorThemePresetId =
  | 'apidex'
  | 'ocean'
  | 'electric'
  | 'ember'
  | 'forest'
  | 'harbor'
  | 'midnight'
  | 'orchid'
  | 'candy'
  | 'walnut';

export interface ColorThemePreset {
  id: ColorThemePresetId;
  light: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
  dark: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
  status: StatusColors;
}

export const COLOR_THEME_PRESETS: readonly ColorThemePreset[] = [
  {
    /** Brand orange + navy; slate accent */
    id: 'apidex',
    light: {
      primary: '#EF5717',
      secondary: '#01358D',
      accent: '#6E848E',
      background: '#F7F9FC',
    },
    dark: {
      primary: '#EF5717',
      secondary: '#01358D',
      accent: '#6E848E',
      background: '#0B1224',
    },
    status: {
      success: '#22C55E',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
    },
  },
  {
    /** Navy primary, sky secondary, sage accent */
    id: 'ocean',
    light: {
      primary: '#446792',
      secondary: '#9BCAE2',
      accent: '#C3C09A',
      background: '#F0F6FA',
    },
    dark: {
      primary: '#446792',
      secondary: '#9BCAE2',
      accent: '#C3C09A',
      background: '#0F1729',
    },
    status: {
      success: '#2D9A8A',
      warning: '#D4A84A',
      error: '#C45C5C',
      info: '#5A8FC5',
    },
  },
  {
    /** Gold primary, ink secondary, silver accent */
    id: 'electric',
    light: {
      primary: '#E3BE3C',
      secondary: '#1F2128',
      accent: '#C2C4CA',
      background: '#F6F5F2',
    },
    dark: {
      primary: '#E3BE3C',
      secondary: '#1F2128',
      accent: '#C2C4CA',
      background: '#12151C',
    },
    status: {
      success: '#5B8A6E',
      warning: '#C9A63A',
      error: '#C05C5C',
      info: '#6E7D94',
    },
  },
  {
    /** Burnt orange, brick red, ember brown */
    id: 'ember',
    light: {
      primary: '#E07A2F',
      secondary: '#C44536',
      accent: '#7A2E1F',
      background: '#FBF3ED',
    },
    dark: {
      primary: '#E07A2F',
      secondary: '#C44536',
      accent: '#7A2E1F',
      background: '#241512',
    },
    status: {
      success: '#6C8F5C',
      warning: '#E0A44A',
      error: '#B73A2D',
      info: '#9C6F5E',
    },
  },
  {
    /** Fresh greens from moss to pine */
    id: 'forest',
    light: {
      primary: '#6BA368',
      secondary: '#3E7C59',
      accent: '#1F4D3A',
      background: '#F1F7F2',
    },
    dark: {
      primary: '#6BA368',
      secondary: '#3E7C59',
      accent: '#1F4D3A',
      background: '#0F1A14',
    },
    status: {
      success: '#4F9363',
      warning: '#C2A94F',
      error: '#B45D4F',
      info: '#5D8D82',
    },
  },
  {
    /** Bright aqua with deep harbor blues */
    id: 'harbor',
    light: {
      primary: '#7EC8E3',
      secondary: '#3A6EA5',
      accent: '#1F3F66',
      background: '#F0F7FB',
    },
    dark: {
      primary: '#7EC8E3',
      secondary: '#3A6EA5',
      accent: '#1F3F66',
      background: '#0F1C2B',
    },
    status: {
      success: '#4F9B87',
      warning: '#D0B14F',
      error: '#C5645F',
      info: '#6DA8D6',
    },
  },
  {
    /** Near-black palette with bold gold contrast */
    id: 'midnight',
    light: {
      primary: '#0F0F0F',
      secondary: '#FFD447',
      accent: '#2A2A2A',
      background: '#F7F3E8',
    },
    dark: {
      primary: '#0F0F0F',
      secondary: '#FFD447',
      accent: '#2A2A2A',
      background: '#0A0A0A',
    },
    status: {
      success: '#6B9A62',
      warning: '#E0B93F',
      error: '#C35A4A',
      info: '#7D8694',
    },
  },
  {
    /** Lavender, rose, and muted plum */
    id: 'orchid',
    light: {
      primary: '#A06CD5',
      secondary: '#D16BA5',
      accent: '#5C4B7D',
      background: '#F7F1FB',
    },
    dark: {
      primary: '#A06CD5',
      secondary: '#D16BA5',
      accent: '#5C4B7D',
      background: '#1A1324',
    },
    status: {
      success: '#6D9A71',
      warning: '#D5B15A',
      error: '#C76179',
      info: '#7B83D2',
    },
  },
  {
    /** Playful pink, aqua, and cherry red */
    id: 'candy',
    light: {
      primary: '#FF8FAB',
      secondary: '#7BDFF2',
      accent: '#C9184A',
      background: '#FFF4F7',
    },
    dark: {
      primary: '#FF8FAB',
      secondary: '#7BDFF2',
      accent: '#C9184A',
      background: '#2A1320',
    },
    status: {
      success: '#61B58A',
      warning: '#FFB347',
      error: '#D9386E',
      info: '#6FCBE5',
    },
  },
  {
    /** Warm browns with a softer walnut accent */
    id: 'walnut',
    light: {
      primary: '#8C5A3C',
      secondary: '#5C3A21',
      accent: '#A47551',
      background: '#F8F3EF',
    },
    dark: {
      primary: '#8C5A3C',
      secondary: '#5C3A21',
      accent: '#A47551',
      background: '#201611',
    },
    status: {
      success: '#69805F',
      warning: '#C49A55',
      error: '#B35A4C',
      info: '#7B8AA0',
    },
  },
] as const;

export function normalizeHex(hex: string): string {
  let h = hex.trim();
  if (!h.startsWith('#')) {
    h = `#${h}`;
  }
  if (h.length === 4) {
    const r = h[1];
    const g = h[2];
    const b = h[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return h.toLowerCase();
}

function hexesEqual(a: string, b: string): boolean {
  return normalizeHex(a) === normalizeHex(b);
}

export interface ThemeSnapshotForMatch {
  lightBackground: string;
  darkBackground: string;
  primaryLight: string;
  secondaryLight: string;
  accentLight: string;
  primaryDark: string;
  secondaryDark: string;
  accentDark: string;
  status: StatusColors;
}

export function matchesPreset(
  snapshot: ThemeSnapshotForMatch,
  preset: ColorThemePreset,
): boolean {
  if (!hexesEqual(snapshot.lightBackground, preset.light.background)) {
    return false;
  }
  if (!hexesEqual(snapshot.darkBackground, preset.dark.background)) {
    return false;
  }
  if (!hexesEqual(snapshot.primaryLight, preset.light.primary)) {
    return false;
  }
  if (!hexesEqual(snapshot.secondaryLight, preset.light.secondary)) {
    return false;
  }
  if (!hexesEqual(snapshot.accentLight, preset.light.accent)) {
    return false;
  }
  if (!hexesEqual(snapshot.primaryDark, preset.dark.primary)) {
    return false;
  }
  if (!hexesEqual(snapshot.secondaryDark, preset.dark.secondary)) {
    return false;
  }
  if (!hexesEqual(snapshot.accentDark, preset.dark.accent)) {
    return false;
  }
  const s = snapshot.status;
  const p = preset.status;
  return (
    hexesEqual(s.success, p.success) &&
    hexesEqual(s.warning, p.warning) &&
    hexesEqual(s.error, p.error) &&
    hexesEqual(s.info, p.info)
  );
}

export function findMatchingPresetId(
  snapshot: ThemeSnapshotForMatch,
): ColorThemePresetId | null {
  for (const preset of COLOR_THEME_PRESETS) {
    if (matchesPreset(snapshot, preset)) {
      return preset.id;
    }
  }
  return null;
}
